"use client";

import { useCallback } from "react";
import { Menu } from "lucide-react";
import { useSession } from "next-auth/react";
import { useAuthSync } from "@/hooks/useAuthSync";
import { ModeToggle } from "@/components/layouts/mode-toggle";
import { SignInButton } from "./sign-in-button";
import { ProfileMemu } from "./profile-menu";
import LogoLink from "./logo-link";
import { Button } from "../ui/button";
import { HomeLink } from "@/components/layouts/home-link";
import { PriceLink } from "@/components/layouts/price-link";
import { TokenLink } from "@/components/layouts/token-link";
import { LogLink } from "@/components/layouts/log-link";
import { ToolLink } from "@/components/layouts/tool-link";
import { StateLink } from "@/components/layouts/state-link";
import { RechargeLink } from "@/components/layouts/recharge-link";
import { DocsLink } from "@/components/layouts/docs-link";
import { NewsLink } from "@/components/layouts/news-link";
import { LanguageToggle } from "@/components/layouts/language";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
} from "../ui/navigation-menu";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "../ui/sheet";
import { Separator } from "../ui/separator";

export function SiteHeader() {
  const { data: session } = useSession();

  // Proactive auth sync: validates session on tab focus and periodic interval
  const handleAuthChange = useCallback((loggedIn: boolean) => {
    // No-op — session state is managed by NextAuth
  }, []);

  useAuthSync(handleAuthChange);

  const user = session?.user ?? null;

  return (
    <header
      className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur-sm supports-backdrop-filter:bg-background/60"
      style={{ "--site-header-height": "84px" } as React.CSSProperties}
    >
      <div className="container mx-auto max-w-8xl flex h-16 items-center px-4 relative">
        {/* Logo */}
        <div className="flex items-center flex-shrink-0">
          <LogoLink />
        </div>

        {/* Desktop nav — shadcn NavigationMenu */}
        <div className="flex-1 flex justify-center">
          <NavigationMenu className="hidden xl:flex w-full" viewport={false}>
            <div className="flex-1 flex justify-center" style={{ marginLeft: 40, marginRight: 220 }}>
              <NavigationMenuList className="gap-8">
                <NavigationMenuItem><HomeLink /></NavigationMenuItem>
                <NavigationMenuItem><PriceLink /></NavigationMenuItem>
                <NavigationMenuItem><TokenLink /></NavigationMenuItem>
                <NavigationMenuItem><LogLink /></NavigationMenuItem>
                <NavigationMenuItem><ToolLink /></NavigationMenuItem>
                <NavigationMenuItem><RechargeLink /></NavigationMenuItem>
                <NavigationMenuItem><StateLink /></NavigationMenuItem>
                <NavigationMenuItem><DocsLink /></NavigationMenuItem>
              </NavigationMenuList>
            </div>
          </NavigationMenu>
        </div>

        {/* Desktop right actions */}
        <div className="hidden xl:flex items-center flex-shrink-0 absolute right-4 top-1/2 -translate-y-1/2">
          <div className="flex items-center space-x-0">
            {user ? (
              <ProfileMemu user={user} onLogout={() => {}} />
            ) : (
              <>
                <SignInButton />
              </>
            )}
            <div className="hidden sm:block ml-4">
              <NewsLink />
            </div>
            <ModeToggle />
            <LanguageToggle />
          </div>
        </div>

        {/* Mobile — shadcn Sheet */}
        <div className="xl:hidden flex items-center justify-end gap-3 ml-auto">
          <ModeToggle />
          <LanguageToggle />

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle>
                  <LogoLink />
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 px-2">
                <SheetClose asChild><div><HomeLink /></div></SheetClose>
                <SheetClose asChild><div><TokenLink /></div></SheetClose>
                <SheetClose asChild><div><LogLink /></div></SheetClose>
                <SheetClose asChild><div><ToolLink /></div></SheetClose>
                <SheetClose asChild><div><StateLink /></div></SheetClose>
                <SheetClose asChild><div><PriceLink /></div></SheetClose>
                <SheetClose asChild><div><RechargeLink /></div></SheetClose>
                <SheetClose asChild><div><DocsLink /></div></SheetClose>
              </nav>
              <Separator />
              <div className="flex items-center justify-between px-4 py-2">
                {user ? (
                  <ProfileMemu user={user} onLogout={() => {}} />
                ) : (
                  <SignInButton />
                )}
                <NewsLink />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
